import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AccesRefuse, Button, PageHeader, StatusBadge } from '@/components/ui';
import { Input } from '@/components/ui/input';
import type { StatusKey } from '@/types';
import { useAuthStore } from '@/store/authStore';
import { type AuditQuery, getAuditFiltres, listJournal } from '@/services/audit';
import { listUtilisateurs } from '@/services/parametres';

const selectCls = 'h-9 w-full rounded-md border border-border bg-surface-secondary px-2 text-sm';

// Action → libellé + couleur (lecture humaine du journal).
const ACTION_UI: Record<string, { label: string; status: StatusKey }> = {
  create: { label: 'Création', status: 'done' },
  update: { label: 'Modification', status: 'working' },
  archive: { label: 'Archivage', status: 'pending' },
  restore: { label: 'Restauration', status: 'done' },
  soft_delete: { label: 'Suppression', status: 'stuck' },
  purge: { label: 'Purge définitive', status: 'stuck' },
};

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Detail({ changed }: { changed: Record<string, [unknown, unknown]> | null }) {
  if (!changed || Object.keys(changed).length === 0) return <span className="text-content-muted">—</span>;
  return (
    <div className="space-y-0.5">
      {Object.entries(changed).map(([champ, [avant, apres]]) => (
        <div key={champ} className="text-xs">
          <span className="font-medium text-content-secondary">{champ}</span>{' '}
          <span className="text-status-stuck/80 line-through">{String(avant ?? '∅')}</span>
          {' → '}
          <span className="text-status-done">{String(apres ?? '∅')}</span>
        </div>
      ))}
    </div>
  );
}

export function JournalPage() {
  const user = useAuthStore((s) => s.user);
  const autorise = user?.role === 'ADMIN' || user?.role === 'DIRECTEUR';

  const [q, setQ] = useState<AuditQuery>({ page: 1, size: 50 });
  const set = (patch: Partial<AuditQuery>) => setQ((p) => ({ ...p, ...patch, page: patch.page ?? 1 }));

  const { data: filtres } = useQuery({ queryKey: ['audit-filtres'], queryFn: getAuditFiltres, enabled: autorise });
  // Liste des utilisateurs pour le filtre « qui » (dégrade en silence si non autorisé).
  const { data: users } = useQuery({ queryKey: ['utilisateurs-mini'], queryFn: listUtilisateurs, enabled: autorise, retry: false });
  const { data, isLoading } = useQuery({
    queryKey: ['journal', q], queryFn: () => listJournal(q), enabled: autorise,
  });

  if (!autorise) {
    return <AccesRefuse message="Accès refusé" detail="Le journal d'activité est réservé à la direction (Directeur / Administrateur)." />;
  }

  const items = data?.items ?? [];
  const page = data?.page ?? 1;
  const pages = data?.pages ?? 1;

  return (
    <>
      <PageHeader
        title="Journal d'activité"
        subtitle="Registre d'audit : qui a créé, modifié, archivé ou supprimé quoi — et quand. Lecture seule."
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: "Journal d'activité" }]}
      />

      {/* Filtres */}
      <div className="mb-4 grid grid-cols-2 gap-3 rounded-lg border border-border bg-surface-secondary p-3 sm:grid-cols-3 lg:grid-cols-6">
        <label className="text-xs text-content-secondary">Utilisateur
          <select className={selectCls} value={q.user_id ?? ''} onChange={(e) => set({ user_id: e.target.value || undefined })}>
            <option value="">Tous</option>
            {users?.map((u) => <option key={u.id} value={u.id}>{u.prenom} {u.nom}</option>)}
          </select>
        </label>
        <label className="text-xs text-content-secondary">Type d'entité
          <select className={selectCls} value={q.entity_type ?? ''} onChange={(e) => set({ entity_type: e.target.value || undefined })}>
            <option value="">Tous</option>
            {filtres?.entity_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="text-xs text-content-secondary">Action
          <select className={selectCls} value={q.action ?? ''} onChange={(e) => set({ action: e.target.value || undefined })}>
            <option value="">Toutes</option>
            {filtres?.actions.map((a) => <option key={a} value={a}>{ACTION_UI[a]?.label ?? a}</option>)}
          </select>
        </label>
        <label className="text-xs text-content-secondary">Du
          <Input type="date" className="h-9" value={q.date_from ?? ''} onChange={(e) => set({ date_from: e.target.value || undefined })} />
        </label>
        <label className="text-xs text-content-secondary">Au
          <Input type="date" className="h-9" value={q.date_to ?? ''} onChange={(e) => set({ date_to: e.target.value || undefined })} />
        </label>
        <div className="flex items-end">
          <Button variant="secondary" className="w-full" onClick={() => setQ({ page: 1, size: 50 })}>Réinitialiser</Button>
        </div>
      </div>

      {/* Tableau (lecture seule) */}
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-surface-primary/40 text-left text-xs text-content-muted">
              <th className="px-3 py-2">Quand</th>
              <th className="px-3 py-2">Qui</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Entité</th>
              <th className="px-3 py-2">Détail (avant → après)</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-content-muted">Chargement…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-content-muted">Aucune activité pour ces filtres.</td></tr>
            ) : items.map((r) => {
              const a = ACTION_UI[r.action] ?? { label: r.action, status: 'pending' as StatusKey };
              return (
                <tr key={r.id} className="border-b border-border/60 last:border-0 align-top">
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums text-content-secondary">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 font-medium text-content-primary">{r.user_label}</td>
                  <td className="px-3 py-2"><StatusBadge status={a.status} label={a.label} /></td>
                  <td className="px-3 py-2 text-content-secondary">{r.entity_type}</td>
                  <td className="px-3 py-2"><Detail changed={r.changed_fields} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-3 flex items-center justify-between text-sm text-content-secondary">
        <span>{data?.total ?? 0} entrée(s) · page {page} / {pages}</span>
        <div className="flex gap-2">
          <Button variant="secondary" disabled={page <= 1} onClick={() => set({ page: page - 1 })}>Précédent</Button>
          <Button variant="secondary" disabled={page >= pages} onClick={() => set({ page: page + 1 })}>Suivant</Button>
        </div>
      </div>
    </>
  );
}
